/**
 * Beweging in de binnenkomer van een case.
 *
 * Dezelfde opbouw als bij een vacature, en met opzet: die twee pagina's openen op dezelfde
 * manier, dus horen ze zich ook hetzelfde te gedragen. Het beeld zakt uit een lichte
 * overmaat terug op zijn plek, de hap linksonder tekent zichzelf, en daaroverheen komen de
 * labels, de titel, de knop en de videokaart na elkaar binnen. De titel komt regel voor
 * regel van onder een masker vandaan.
 *
 * Tijdens het scrollen loopt de tekst iets vooruit op de pagina en blijft het beeld juist
 * achter. Alleen dat verschil zie je. De kaart rechts doet daar niet aan mee -- die staat
 * onder lg onder de tekst in plaats van ernaast -- vandaar dat de parallax alleen vanaf lg
 * loopt.
 *
 * set() en to() met expliciete waarden, geen from(): een from()-tween leest zijn eindwaarde
 * af op het moment dat hij wordt aangemaakt, en legt dan bij een tweede opbouw de
 * begintoestand vast als eindtoestand.
 *
 * Het splitsen van de titel staat in hero-motion.js, gedeeld met de andere hero's.
 *
 * Progressive enhancement: zonder JS -- of met prefers-reduced-motion -- staat alles gewoon
 * op zijn plek. Gaat er onverhoopt iets mis nadat de begintoestand is gezet, dan worden alle
 * inline stijlen weer weggehaald: een binnenkomer die niet beweegt is een schoonheidsfout,
 * een binnenkomer die onzichtbaar blijft is een kapotte pagina.
 *
 * Maten: resources/styles/common/project-hero.scss.
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { EASE, minderBeweging, onthulRegels } from './hero-motion';

gsap.registerPlugin(ScrollTrigger);

export default function initProjectHero() {
  const hero = document.querySelector('[data-project-hero]');

  if (!hero || minderBeweging()) {
    return;
  }

  const media = hero.querySelector('[data-project-hero-media]');
  const notch = hero.querySelector('[data-project-hero-notch]');
  const body = hero.querySelector('[data-project-hero-body]');
  const heading = hero.querySelector('[data-project-hero-heading]');
  const reveals = Array.from(hero.querySelectorAll('[data-project-hero-reveal]'));
  const cards = Array.from(hero.querySelectorAll('[data-project-hero-card]'));

  const beweegbaar = [media, notch, ...reveals, ...cards].filter(Boolean);

  try {
    // De begintoestand meteen, nog voor het lettertype binnen is: anders zie je de hero
    // eerst compleet staan en dan alsnog opbouwen.
    if (media) {
      gsap.set(media, { scale: 1.08 });
    }

    if (notch) {
      gsap.set(notch, { scaleX: 0 });
    }

    gsap.set(reveals, { y: 20, opacity: 0 });
    gsap.set(cards, { x: 36, opacity: 0 });

    parallax(hero, media, body);
    speelIntro(media, notch, reveals, cards);

    // De titel wacht op het lettertype; hero-motion.js regelt dat en het splitsen.
    onthulRegels(heading, { klasse: 'project-hero__line' });
  } catch (fout) {
    // Liever geen beweging dan een onzichtbare binnenkomer.
    gsap.set(beweegbaar, { clearProps: 'all' });

    throw fout;
  }
}

/**
 * De opbouw zelf. Absolute posities op de tijdlijn ("0.35") en geen relatieve: dan
 * verschuift er niets zodra een onderdeel wegvalt bij een case zonder labels of film.
 */
function speelIntro(media, notch, reveals, cards) {
  const intro = gsap.timeline();

  // Het beeld komt uit een lichte overmaat terug op zijn plek. Traag en over een lange
  // duur: een snelle zoom leest als een effect, deze leest als een camera die tot rust
  // komt. Die overmaat is meteen de speling die de parallax nodig heeft.
  if (media) {
    intro.to(media, { scale: 1, duration: 2.2, ease: EASE }, 0);
  }

  // scaleX in plaats van width, dan hoeft de browser per frame niets opnieuw uit te
  // rekenen; transform-origin staat links in de SCSS.
  if (notch) {
    intro.to(notch, { scaleX: 1, duration: 1.2, ease: EASE }, 0.55);
  }

  // Labels en knop kort na elkaar, van onderaf. Klein gehouden -- deze regels moeten
  // binnenkomen, niet opvallen.
  if (reveals.length) {
    intro.to(reveals, { y: 0, opacity: 1, duration: 0.9, ease: EASE, stagger: 0.07 }, 0.35);
  }

  // De kaart rechts komt van opzij: hij hoort bij het beeld en niet bij de tekstkolom, en
  // die andere richting maakt dat zichtbaar.
  if (cards.length) {
    intro.to(cards, { x: 0, opacity: 1, duration: 1, ease: EASE, stagger: 0.12 }, 0.6);
  }

  return intro;
}

/**
 * Parallax terwijl de binnenkomer uit beeld scrollt, alleen waar de kaart naast de tekst
 * staat. In pixels en niet in procenten: de hero is op mobiel veel hoger dan op desktop,
 * dus procenten zouden daar heel anders uitpakken.
 */
function parallax(hero, media, body) {
  if (!media && !body) {
    return;
  }

  gsap.matchMedia().add('(min-width: 1024px)', () => {
    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: hero,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });

    if (media) {
      timeline.to(media, { y: 70, ease: 'none' }, 0);
    }

    if (body) {
      timeline.to(body, { y: -50, ease: 'none' }, 0);
    }
  });
}
